import React, { useEffect, useMemo, useState } from "react"
import { Box, Flex } from "@chakra-ui/react"
import Modal from "components/dialogs/Modal"
import { DioboxEvent, SubscriptionType, TextMessage } from "sharedTypes"
import { useGuests } from "queries/guests"
import Spinner from "components/elements/Spinner"
import UpgradeAlert from "components/elements/UpgradeAlert"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as Yup from "yup"
import AlertBox, { AlertBoxType } from "components/elements/AlertBox/AlertBox"
import { SENDER_PHONE_NUMBER } from "utilities/constants"
import { asDate } from "utilities/strings"
import { sampleGuestFor } from "utilities/sampleGuestData"
import useMergeTags from "services/useMergeTags"
import { sendTextMessageTest } from "api/texts"
import { successToast } from "utilities/toasts"
import validatePhoneNumber from "utilities/validatePhoneNumber"
import { buildResolvedVariables, getTemplateMediaUrl } from "utilities/whatsappTemplates"
import useWhatsAppTemplate from "services/useWhatsAppTemplate"
import TemplateUnavailable from "components/TemplateEditor/TemplateUnavailable"
import Phone from "components/Phone/Phone"
import SMSScreen from "./Phone/SMSScreen"
import WhatsAppScreen from "./Phone/WhatsAppScreen"
import GuestSelector from "./GuestSelector"
import SendTestMessage from "./SendTestMessage"

type Props = {
  onHide: () => void
  event: DioboxEvent
  textMessage: TextMessage
  enabled: boolean
}

export const FIRST_X_NUMBER_OF_GUESTS_TO_SHOW = 10

const PreviewTextMessageSchema = Yup.object().shape({
  to: Yup.string()
    .test("is-valid-phone", "Phone number is not valid", (value) => validatePhoneNumber(value!))
    .required(),
  body: Yup.string().min(1).required(),
})

const PreviewTextMessage = ({
  onHide,
  event: {
    title, publicEventTitle, startDate, id: eventId, urls,
  },
  textMessage,
  enabled,
}: Props) => {
  const eventTitle = publicEventTitle || title
  const {
    body: bodyTemplate,
    channel,
    whatsappContentSid,
    whatsappContentVariables,
  } = textMessage

  const { template, templateUnavailable } = useWhatsAppTemplate(whatsappContentSid)

  const [selectedGuestIndex, setSelectedGuestIndex] = useState(0)
  const { guests, isLoading } = useGuests(eventId)

  const {
    control,
    formState: { isSubmitting, isValid },
    handleSubmit: handleFormSubmit,
    resetField,
    setValue,
    watch,
  } = useForm<{ to: string; body: string }>({
    defaultValues: { to: "", body: "" },
    resolver: yupResolver(PreviewTextMessageSchema),
    mode: "onChange",
  })

  const firstXGuests = guests.length > 0 ? guests.slice(0, FIRST_X_NUMBER_OF_GUESTS_TO_SHOW) : []
  const hasGuests = firstXGuests.length > 0
  const sampleGuest = useMemo(() => sampleGuestFor(urls?.portal), [urls?.portal])
  const selectedGuest = hasGuests ? firstXGuests[selectedGuestIndex] : sampleGuest

  const { replaceMergeTags } = useMergeTags({ eventId })

  const date = asDate(startDate)!

  // resolvedVariables still needed for WhatsApp test sends (arbitrary phone number, not a guest)
  const resolvedVariables = useMemo((): Record<string, string> => {
    if (!template || !whatsappContentVariables) return {}

    return buildResolvedVariables(
      template.variables,
      whatsappContentVariables,
      (mergeTag) => replaceMergeTags(mergeTag, eventTitle, date, selectedGuest),
    )
  }, [template, whatsappContentVariables, replaceMergeTags, eventTitle, date, selectedGuest])

  const handleSubmit = async (values: { to: string; body: string }) => {
    const testPayload = channel === "whatsapp"
      ? {
        to: values.to,
        channel: "whatsapp" as const,
        contentSid: whatsappContentSid,
        contentVariables: resolvedVariables,
      }
      : { to: values.to, body: values.body }

    try {
      await sendTextMessageTest(eventId, testPayload)
      successToast({ title: `Sent message to: ${values.to}` })
      resetField("to")
    } catch {
      // Errors are surfaced by the global axios interceptor (utilities/toasts#handleError)
    }
  }

  useEffect(() => {
    setValue("body", replaceMergeTags(bodyTemplate, eventTitle, date, selectedGuest))
  }, [replaceMergeTags, bodyTemplate, eventTitle, date, selectedGuest, setValue])

  const populatedTextBody = watch("body") ?? ""

  const mediaUrl = template
    ? getTemplateMediaUrl(template, whatsappContentVariables, resolvedVariables)
    : undefined

  const templateButtons = template?.buttons

  const renderContent = () => {
    if (isLoading) return <Spinner />

    if (templateUnavailable) {
      return (
        <Flex>
          <Flex w="60%" borderRightWidth={1} justifyContent="center" p={14} minH={500}>
            <TemplateUnavailable />
          </Flex>
          <Box flex={1} pt={8}>
            <form onSubmit={handleFormSubmit(handleSubmit)}>
              <SendTestMessage
                control={control}
                channel={channel}
                isSubmitting={isSubmitting}
                disabled
              />
            </form>
          </Box>
        </Flex>
      )
    }

    return (
      <Flex>
        <Flex w="60%" borderRightWidth={1} justifyContent="center" pt={14}>
          <Phone>
            {channel === "whatsapp" ? (
              <WhatsAppScreen
                text={populatedTextBody}
                mediaUrl={mediaUrl}
                buttons={templateButtons}
              />
            ) : (
              <SMSScreen phoneNumber={SENDER_PHONE_NUMBER} text={populatedTextBody} />
            )}
          </Phone>
        </Flex>
        <Box flex={1} pt={8}>
          {hasGuests && (
            <GuestSelector
              selectedGuest={firstXGuests[selectedGuestIndex]}
              selectedGuestIndex={selectedGuestIndex}
              setSelectedGuestIndex={setSelectedGuestIndex}
              guestCount={firstXGuests.length}
            />
          )}
          <form onSubmit={handleFormSubmit(handleSubmit)}>
            <SendTestMessage
              control={control}
              channel={channel}
              isSubmitting={isSubmitting}
              disabled={!enabled || !isValid || !hasGuests}
            />
          </form>
          {!enabled ? (
            <Box p={6}>
              <UpgradeAlert
                text="Upgrade to Enterprise to unlock SMS and WhatsApp messaging features."
                defaultSelectedPlan={SubscriptionType.Enterprise}
              />
            </Box>
          ) : !hasGuests && (
            <Box p={6}>
              <AlertBox
                type={AlertBoxType.Upgrade}
                text="Add at least one guest to preview and send a test message."
              />
            </Box>
          )}
        </Box>
      </Flex>
    )
  }

  const modalTitle = channel === "whatsapp" ? "WhatsApp Preview" : "SMS Preview"

  return (
    <Modal size="5xl" title={modalTitle} show noPadding hide={onHide}>
      {renderContent()}
    </Modal>
  )
}

export default PreviewTextMessage
